import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, MapPin, Clock, Users, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PageHero } from "@/components/PageHero";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/lib/assets";

const MODE_STYLE = { "In-Person": "bg-navy-900 text-white", Virtual: "bg-verified text-white", Hybrid: "bg-gold text-white" };

function RSVPDialog({ event }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await api.post("/events/rsvp", { event_id: event.id, name, email });
      toast.success(data.message); setOpen(false); setName(""); setEmail("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-navy-900 hover:bg-navy-700 text-white" data-testid={`event-rsvp-${event.id}`}>RSVP</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">RSVP · {event.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-11 rounded-xl" data-testid="rsvp-name" /></div>
          <div><Label>Email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-11 rounded-xl" data-testid="rsvp-email" /></div>
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-gold hover:bg-gold-light text-white" data-testid="rsvp-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm RSVP"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get("/events").then((r) => setEvents(r.data)).catch(() => {}); }, []);
  return (
    <main>
      <PageHero overline="Community Events & Workshops" image={IMG.event1}
        title="Learn, connect, and grow together"
        subtitle="Free workshops, webinars, and town halls hosted by the Foundation. Reserve your spot below." />
      <section className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-20 space-y-6">
        {events.map((ev) => (
          <div key={ev.id} className="card-hover bg-white rounded-2xl border border-slate-200 p-7 flex flex-col md:flex-row md:items-center gap-6" data-testid={`event-card-${ev.id}`}>
            <div className="md:w-28 shrink-0 text-center md:border-r md:border-slate-100 md:pr-6">
              <div className="font-display text-3xl font-bold text-navy-900">{new Date(ev.date).getDate()}</div>
              <div className="text-sm uppercase tracking-wider text-gold font-semibold">{new Date(ev.date).toLocaleString("en-US", { month: "short" })}</div>
              <div className="text-xs text-slate-400">{new Date(ev.date).getFullYear()}</div>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`rounded-full ${MODE_STYLE[ev.mode] || "bg-slate-200"}`}>{ev.mode}</Badge>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{ev.type}</span>
              </div>
              <h3 className="font-display text-xl font-bold text-navy-900">{ev.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{ev.description}</p>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-navy-700" /> {ev.time}</span>
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-navy-700" /> {ev.location}</span>
                <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-navy-700" /> {ev.capacity} seats</span>
              </div>
            </div>
            <div className="shrink-0"><RSVPDialog event={ev} /></div>
          </div>
        ))}
      </section>
    </main>
  );
}
